import {
  Directive,
  ElementRef,
  EmbeddedViewRef,
  Input,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { AppRole } from '@core/api/me';
import { CurrentUserService } from '@core/auth/current-user.service';

const DISABLED_CLASS = 'app-has-role-disabled';

@Directive({
  selector: '[appHasRole], [appHasRoleDisabled]',
  standalone: true,
})
export class HasRoleDirective {
  private readonly templateRef = inject(TemplateRef<unknown>, { optional: true });
  private readonly viewContainerRef = inject(ViewContainerRef, { optional: true });
  private readonly elementRef = inject(ElementRef<HTMLElement>, { optional: true });
  private readonly renderer = inject(Renderer2);
  private readonly currentUserService = inject(CurrentUserService);

  private roles: readonly AppRole[] = [];
  private disableIfNoRole = false;
  private isDisabled = false;

  private embeddedView: EmbeddedViewRef<unknown> | null = null;
  private disabledHost: HTMLElement | null = null;

  @Input('appHasRole')
  set appHasRole(roles: AppRole | readonly AppRole[]) {
    if (roles === undefined || roles === null) {
      return;
    }
    this.roles = Array.isArray(roles) ? roles : [roles as AppRole];
    this.updateView();
  }

  @Input()
  set appHasRoleDisabled(value: boolean | string) {
    this.setDisabled(value);
  }

  @Input()
  set appHasRoleAppHasRoleDisabled(value: boolean | string) {
    this.setDisabled(value);
  }

  private setDisabled(value: boolean | string): void {
    if (value === undefined || value === null) {
      return;
    }
    this.disableIfNoRole = value === '' || value === true || value === 'true';
    this.updateView();
  }

  private updateView(): void {
    const hasAccess = this.currentUserService.hasAnyRole(this.roles);

    if (hasAccess) {
      this.enable();
      return;
    }

    if (this.disableIfNoRole) {
      this.disable();
      return;
    }

    this.remove();
  }

  private enable(): void {
    if (this.isDisabled) {
      this.removeDisabledStyles();
      this.isDisabled = false;
      this.disabledHost = null;
    }

    if (this.isStructural()) {
      if (this.viewContainerRef && this.templateRef && !this.embeddedView) {
        this.embeddedView = this.viewContainerRef.createEmbeddedView(this.templateRef);
      }
      return;
    }
  }

  private disable(): void {
    if (this.isStructural()) {
      if (!this.viewContainerRef || !this.templateRef) {
        return;
      }

      if (!this.embeddedView) {
        this.embeddedView = this.viewContainerRef.createEmbeddedView(this.templateRef);
      }

      if (this.isDisabled) {
        return;
      }

      const host = this.getFirstElementNode(this.embeddedView);
      if (!host) {
        return;
      }

      this.applyDisabledStyles(host);
      this.disabledHost = host;
      this.isDisabled = true;
      return;
    }

    // Attribute form
    const host = this.elementRef?.nativeElement;
    if (!host) {
      return;
    }

    if (this.isDisabled) {
      return;
    }

    this.applyDisabledStyles(host);
    this.disabledHost = host;
    this.isDisabled = true;
  }

  private remove(): void {
    if (this.isDisabled) {
      this.removeDisabledStyles();
      this.isDisabled = false;
      this.disabledHost = null;
    }

    if (this.isStructural()) {
      this.clearEmbeddedView();
    } else if (this.viewContainerRef) {
      this.viewContainerRef.clear();
    }
  }

  private applyDisabledStyles(host: HTMLElement): void {
    this.renderer.addClass(host, DISABLED_CLASS);
    this.renderer.setAttribute(host, 'aria-disabled', 'true');
    this.renderer.setAttribute(host, 'tabindex', '-1');

    if (this.isNativeDisabledElement(host)) {
      this.renderer.setProperty(host, 'disabled', true);
    }
  }

  private removeDisabledStyles(): void {
    const host = this.disabledHost ?? this.elementRef?.nativeElement;
    if (!host) {
      return;
    }

    this.renderer.removeClass(host, DISABLED_CLASS);
    this.renderer.removeAttribute(host, 'aria-disabled');
    this.renderer.removeAttribute(host, 'tabindex');

    if (this.isNativeDisabledElement(host)) {
      this.renderer.setProperty(host, 'disabled', false);
    }

    this.disabledHost = null;
  }

  private clearEmbeddedView(): void {
    if (this.embeddedView) {
      const index = this.viewContainerRef?.indexOf(this.embeddedView);
      if (index !== undefined && index >= 0 && this.viewContainerRef) {
        this.viewContainerRef.remove(index);
      } else if (this.viewContainerRef) {
        this.viewContainerRef.clear();
      }
      this.embeddedView = null;
      return;
    }

    if (this.viewContainerRef) {
      this.viewContainerRef.clear();
    }
  }

  private getFirstElementNode(view: EmbeddedViewRef<unknown>): HTMLElement | null {
    for (const node of view.rootNodes) {
      if (node.nodeType === 1) {
        return node as HTMLElement;
      }
    }
    return null;
  }

  private isStructural(): boolean {
    return !!this.templateRef && !!this.viewContainerRef;
  }

  private isNativeDisabledElement(
    el: HTMLElement,
  ): el is HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
    const tag = el.tagName;
    return tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
  }
}
